import React, { useState, useEffect, createContext, useContext, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Calendar, Users, Clock, FileText, MessageSquare, Settings, LogOut, Plus, Check, X, CalendarDays, Send, Trash2, Edit, ChevronLeft, ChevronRight, MapPin, Building2, AlertTriangle, AlertCircle, Package, Eye, Link, Upload, Bell, Menu, Copy, Download, FileDown, Smartphone, Phone, UserPlus, ArrowLeft, LayoutGrid, Filter, RefreshCw, MoreHorizontal, ChevronDown, KeyRound, DoorOpen, List, Stethoscope, ClipboardList, Armchair, Shield, Search, RotateCcw, Star, Pencil, User, BarChart3, Save, FolderOpen, StickyNote, ScrollText, Brain, Layers, Scale, BookOpen, Calculator, Sun, Moon, Command, Receipt, TrendingUp, CreditCard, Truck, Inbox, HandCoins, Mic, Euro, Droplets, QrCode, Gauge } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { ChoixOngletDoctipro } from "./components/doctipro/ChoixOngletDoctipro";
import { DoctiproDock } from "./components/shared/PatientDoctiproCard";
import PortailCorrespondant from "./components/correspondants/PortailCorrespondant";
import CorrespondantsManager from "./components/correspondants/CorrespondantsManager";
import { toast } from "sonner";
import usePWA from './hooks/usePWA';

// Shared modules
import { BACKEND_URL, API } from "./shared/config";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PointageRappel } from "./components/navigation/PointageRappel";
import { PlanningProvider } from "./contexts/PlanningContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import CommandPalette from "./components/CommandPalette";
import { getPhotoUrl, PhotoWithFallback, sortEmployeesByRoleThenName, filterEmployeesBySearch } from "./shared/utils";
import { canViewRubrique } from "./shared/permissions";
import NavHome from "./components/navigation/NavHome";
import NavHomeFutur from "./components/navigation/NavHomeFutur";
import { SCREENS, buildCtx, buildTree, findPage, resolveLegacy } from "./navigation/navConfig";
import { RubriqueAccess } from "./components/admin/RubriqueAccess";
import { hasDroit, useDroits, estDirecteur } from "./lib/droits";
import { routeForNotif } from "./navigation/notifRoutes";
import { demanderOuverture } from "./lib/ouvrirElement";
import { useNavBadges } from "./navigation/useNavBadges";
import { useEspaceResume } from "./components/espace/useEspaceResume";
import { getNavMode } from "./navigation/navPref";

// Extracted components — chargés à la demande (code splitting)
// Login & Navigation restent eagers (toujours visibles)
import Navigation from './components/navigation/Navigation';
import { ConsignesBandeau } from './components/consignes/ConsignesBandeau';
import LoginPage from './components/auth/LoginPage';
import AlerteFactureImpayee from './components/comptabilite/AlerteFactureImpayee';

// Helper : lazy() avec retry automatique sur ChunkLoadError.
// Force un reload une seule fois (sentinelle sessionStorage) si un chunk échoue,
// utile après un déploiement Render quand le navigateur garde l'ancien fichier en cache.
const lazyWithRetry = (componentImport) => lazy(async () => {
  const KEY = 'chunk-reload-attempted';
  try {
    return await componentImport();
  } catch (error) {
    const isChunkError = error?.name === 'ChunkLoadError'
      || /Loading chunk \d+ failed/i.test(error?.message || '')
      || /Failed to fetch dynamically imported module/i.test(error?.message || '');
    if (isChunkError && !sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, Date.now().toString());
      window.location.reload();
      // On lève l'erreur pendant le reload pour que React n'essaie pas de rendre
      throw error;
    }
    throw error;
  }
});

// Tous les managers : lazy loading → premier paint ~3-5x plus rapide
const ChatManager = lazyWithRetry(() => import('./components/chat/ChatManager'));
const ActualitesManager = lazyWithRetry(() => import('./components/dashboard/ActualitesManager'));
const DossiersATraiterManager = lazyWithRetry(() => import('./components/dashboard/DossiersATraiterManager'));
const CongeManager = lazyWithRetry(() => import('./components/conges/CongeManager'));
const QuizzManager = lazyWithRetry(() => import('./components/dashboard/QuizzManager'));
const NotesManager = lazyWithRetry(() => import('./components/dashboard/NotesManager'));
const MaterielBlocManager = lazyWithRetry(() => import('./components/dashboard/MaterielBlocManager'));
const BlocOperatoireManager = lazyWithRetry(() => import('./components/dashboard/BlocOperatoireManager'));
const AideDecisionManager = lazyWithRetry(() => import('./components/dashboard/AideDecisionManager'));
const BibliothequeManager = lazyWithRetry(() => import('./components/dashboard/BibliothequeManager'));
const StrabismusSimulator = lazyWithRetry(() => import('./components/dashboard/StrabismusSimulator'));
const PreconsultManager = lazyWithRetry(() => import('./components/dashboard/PreconsultManager'));
const EyeAnatomy3D = lazyWithRetry(() => import('./components/dashboard/EyeAnatomy3D'));
const IOLCalculator = lazyWithRetry(() => import('./components/dashboard/IOLCalculator'));
const RefractiveDecisionTool = lazyWithRetry(() => import('./components/dashboard/RefractiveDecisionTool'));
const OCROrdonnance = lazyWithRetry(() => import('./components/dashboard/OCROrdonnance'));
const PlanningDiagnostic = lazyWithRetry(() => import('./components/admin/PlanningDiagnostic'));
const ProjetsManager = lazyWithRetry(() => import('./components/dashboard/ProjetsManager'));
const GrilleSalarialeManager = lazyWithRetry(() => import('./components/dashboard/GrilleSalarialeManager'));
const QualiteManager = lazyWithRetry(() => import('./components/qualite/QualiteManager'));
const FichierASignerPopup = lazyWithRetry(() => import('./components/dashboard/FichiersASigner').then(m => ({ default: m.FichierASignerPopup })));
const LegionelloseManager = lazyWithRetry(() => import('./components/ressources/LegionelloseManager'));
const FacturationHtml = lazyWithRetry(() => import('./components/ressources/FacturationHtml'));
const IcdHtml = lazyWithRetry(() => import('./components/ressources/IcdHtml'));
const IshiharaReleve = lazyWithRetry(() => import('./components/ressources/IshiharaReleve'));
const OsdiQuestionnaire = lazyWithRetry(() => import('./components/ressources/OsdiQuestionnaire'));
const CommentaireDirection = lazyWithRetry(() => import('./components/ressources/CommentaireDirection'));
const RecapMensuelManager = lazyWithRetry(() => import('./components/dashboard/RecapMensuelManager'));
const CurriculumVitaeManager = lazyWithRetry(() => import('./components/dashboard/CurriculumVitaeManager'));
const PrimesManager = lazyWithRetry(() => import('./components/dashboard/PrimesManager'));
const PatientsAdresserManager = lazyWithRetry(() => import('./components/dashboard/PatientsAdresserManager'));
const DemandesExamenManager = lazyWithRetry(() => import('./components/dashboard/DemandesExamenManager'));
const PlanningManager = lazyWithRetry(() => import('./components/planning/PlanningManager'));
const PlanCabinetCompact = lazyWithRetry(() => import('./components/planning/PlanCabinetCompact'));
const SemainesTypesManager = lazyWithRetry(() => import('./components/planning/SemainesTypesManager'));
const PlanCabinetManager = lazyWithRetry(() => import('./components/planning/PlanCabinetManager'));
const DemandesTravailManager = lazyWithRetry(() => import('./components/demandes/DemandesTravailManager'));
const TachesDuJourManager = lazyWithRetry(() => import('./components/taches/TachesDuJourManager'));
const AccueilAtcdManager = lazyWithRetry(() => import('./components/accueil/AccueilAtcdManager'));
const BorneApp = lazyWithRetry(() => import('./components/borne/BorneApp'));
const AvertissementsManager = lazyWithRetry(() => import('./components/taches/AvertissementsManager'));
const ReglementsManager = lazyWithRetry(() => import('./components/taches/ReglementsManager'));
const CoffreFortManager = lazyWithRetry(() => import('./components/coffre-fort/CoffreFortManager'));
const ComptabiliteManager = lazyWithRetry(() => import('./components/comptabilite/ComptabiliteManager'));
const AdminManager = lazyWithRetry(() => import('./components/admin/AdminManager'));
const AutresParametres = lazyWithRetry(() => import('./components/admin/AutresParametres'));
const BornesParametres = lazyWithRetry(() => import('./components/admin/BornesParametres'));
const AutorisationsManager = lazyWithRetry(() => import('./components/admin/AutorisationsManager'));
const AttributionManager = lazyWithRetry(() => import('./components/admin/AttributionManager'));
const CentreFavoriManager = lazyWithRetry(() => import('./components/admin/CentreFavoriManager'));
const CentresManager = lazyWithRetry(() => import('./components/centres/CentresManager'));
const StocksManager = lazyWithRetry(() => import('./components/stocks/StocksManager'));
const StockGlobalManager = lazyWithRetry(() => import('./components/stocks/StockGlobalManager'));
const PersonnelManager = lazyWithRetry(() => import('./components/personnel/PersonnelManager'));
const AnnuaireManager = lazyWithRetry(() => import('./components/personnel/AnnuaireManager'));
const ComptesMdpManager = lazyWithRetry(() => import('./components/personnel/ComptesMdpManager'));
const CartesManager = lazyWithRetry(() => import('./components/personnel/CartesManager'));
const MaterielInventaireManager = lazyWithRetry(() => import('./components/personnel/MaterielInventaireManager'));
const ChampVisuelManager = lazyWithRetry(() => import('./components/dashboard/ChampVisuelManager'));
const DemandeExamenHub = lazyWithRetry(() => import('./components/dashboard/DemandeExamenHub'));
const DemandeExamenManager = lazyWithRetry(() => import('./components/dashboard/DemandeExamenManager'));
const InfosPatientManager = lazyWithRetry(() => import('./components/dashboard/InfosPatientManager'));
const OrdonnancesManager = lazyWithRetry(() => import('./components/ordonnances/OrdonnancesManager'));
const SallesManager = lazyWithRetry(() => import('./components/salles/SallesManager'));
const MonProfilManager = lazyWithRetry(() => import('./components/profile/MonProfilManager'));
const PasserellePage = lazyWithRetry(() => import('./components/shared/PasserellePage'));
const DocsTutosManager = lazyWithRetry(() => import('./components/docs/DocsTutosManager'));
const NotificationsAdminManager = lazyWithRetry(() => import('./components/admin/NotificationsAdminManager'));
const RgpdManager = lazyWithRetry(() => import('./components/admin/RgpdManager'));
const PareFeuManager = lazyWithRetry(() => import('./components/admin/PareFeuManager'));
const ConnexionDoctiproManager = lazyWithRetry(() => import('./components/doctipro/ConnexionDoctiproManager'));
const EspaceTravail = lazyWithRetry(() => import('./components/espace/EspaceTravail'));
const CabinetPlanWithPopup = lazyWithRetry(() => import('./components/dashboard/CabinetPlanWithPopup'));

// Fallback de chargement pour Suspense
const ModuleLoader = () => (
  <div className="flex items-center justify-center min-h-[40vh] text-gray-400">
    <div className="flex flex-col items-center gap-2">
      <div className="h-8 w-8 rounded-full border-2 border-[#1173D4] border-t-transparent animate-spin"></div>
      <span className="text-sm">Chargement…</span>
    </div>
  </div>
);



const PWAInstallBanner = () => {
  const { isInstallable, isInstalled, isIOS, installApp, iOSInstructions } = usePWA();
  const [showBanner, setShowBanner] = useState(true);
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Ne pas afficher si déjà installé ou si l'utilisateur a fermé le banner
  if (isInstalled || !showBanner) return null;

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
    } else {
      const success = await installApp();
      if (success) {
        toast.success('Application installée avec succès !');
        setShowBanner(false);
      }
    }
  };

  // Afficher seulement si installable (Chrome/Edge) ou iOS
  if (!isInstallable && !isIOS) return null;

  return (
    <>
      {/* Petit bouton d'installation discret */}
      <button 
        onClick={handleInstall}
        className="pwa-install-floating-btn"
        title="Installer l'application"
      >
        <Download className="h-4 w-4" />
        <span>Installer</span>
        <button 
          onClick={(e) => { e.stopPropagation(); setShowBanner(false); }}
          className="pwa-close-btn"
        >
          <X className="h-3 w-3" />
        </button>
      </button>

      {/* Modal iOS avec instructions */}
      <Dialog open={showIOSModal} onOpenChange={setShowIOSModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-blue-600" />
              Installer sur iPhone/iPad
            </DialogTitle>
            <DialogDescription>
              Suivez ces étapes pour ajouter l'application à votre écran d'accueil
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">1</div>
              <div>
                <p className="font-medium">Appuyez sur le bouton Partager</p>
                <p className="text-sm text-gray-500">Le bouton avec une flèche vers le haut (⬆️) en bas de Safari</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">2</div>
              <div>
                <p className="font-medium">Sélectionnez "Sur l'écran d'accueil"</p>
                <p className="text-sm text-gray-500">Faites défiler vers le bas si nécessaire</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">3</div>
              <div>
                <p className="font-medium">Appuyez sur "Ajouter"</p>
                <p className="text-sm text-gray-500">L'icône OphtaCare apparaîtra sur votre écran d'accueil</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setShowIOSModal(false)}>J'ai compris</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};


const NotificationToday = () => {
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodayNotification();
  }, []);

  const fetchTodayNotification = async () => {
    try {
      const response = await axios.get(`${API}/notifications/me/today`);
      setNotification(response.data);
    } catch (error) {
      console.error('Aucune notification pour aujourd\'hui');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !notification) return null;

  return (
    <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-blue-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-blue-800 flex items-center space-x-2">
          <CalendarDays className="h-4 w-4" />
          <span>Planning du jour</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-sm text-blue-700 whitespace-pre-line">
          {notification.contenu}
        </div>
      </CardContent>
    </Card>
  );
};


// PushNotificationManager and DevicesList extracted to /components/notifications/PushNotificationManager.jsx


// Le rôle Correspondant n'accède qu'à son portail sécurisé
const EspaceSelonRole = () => {
  const { user } = useAuth();
  if (user?.role === 'Correspondant') return <PortailCorrespondant />;
  return <PlanningProvider><Dashboard /></PlanningProvider>;
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('actualites');
  const [actualitesSubTab, setActualitesSubTab] = useState('news');
  const [ressourcesSubTab, setRessourcesSubTab] = useState('bloc-op');
  const [blocSubTabRaw, setBlocSubTab] = useState('bloc-op');
  const [ressourcesNewSubTab, setRessourcesNewSubTab] = useState('bibliotheque');
  const [comptabiliteSubTab, setComptabiliteSubTab] = useState('caisse-factures');
  const [equipeSubTab, setEquipeSubTab] = useState('personnel');
  const [autorisationsTab, setAutorisationsTab] = useState(null);
  const [corrHabilite, setCorrHabilite] = useState(false);
  const [qualiteHabilite, setQualiteHabilite] = useState(false);
  useEffect(() => {
    axios.get(`${API}/correspondants/acces`).then(r => setCorrHabilite(r.data.habilite)).catch(() => {});
    axios.get(`${API}/qualite/acces`).then(r => setQualiteHabilite(r.data.habilite ? r.data : false)).catch(() => {});
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cmdKOpen, setCmdKOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const { user, centreActif, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  // Mode d'affichage : jour (classique) ou nuit (habillage sombre futuriste)
  const navMode = getNavMode(user);
  const NUIT = navMode === 'nuit';
  useEffect(() => {
    document.documentElement.classList.toggle('opn-fx', !!user && NUIT);
    return () => document.documentElement.classList.remove('opn-fx');
  }, [NUIT, user]);

  // Page d'accueil (présentation) ouverte par le bouton « OP »
  const [accueil, setAccueil] = useState(false);
  const { ready: droitsReady } = useDroits(user);
  const canViewSubForTree = useCallback((rubrique) => hasDroit(user, rubrique, 'voir'), [user, droitsReady]);
  const tree = useMemo(() => buildTree(buildCtx(user, corrHabilite, (r) => canViewRubrique(user, r), canViewSubForTree, { qualiteHabilite })), [user, corrHabilite, qualiteHabilite, canViewSubForTree, droitsReady]);
  const badges = useNavBadges(user, tree, `${activeTab}:${accueil ? 'accueil' : ''}`);
  const espace = useEspaceResume(user);
  const allerEspace = useCallback(() => { setAccueil(false); setActiveTab('espace'); window.scrollTo({ top: 0 }); }, []);

  // Écran de l'arborescence → onglet + sous-onglet de la navigation
  const goAncienne = useCallback((screenId, ecran) => {
    const [tab, sub] = (SCREENS[screenId]?.legacy || [])[0] || [];
    if (!tab) return;
    setAccueil(false);
    setActiveTab(tab);
    if (!sub) return;
    if (tab === 'actualites') setActualitesSubTab(sub);
    else if (tab === 'bloc') setBlocSubTab(sub);
    else if (tab === 'ressources') { setRessourcesNewSubTab(sub); setRessourcesSubTab(sub); }
    else if (tab === 'comptabilite') setComptabiliteSubTab(sub);
    else if (tab === 'personnel' || tab === 'equipe') setEquipeSubTab(sub);
  }, []);
  const goScreen = useCallback((screenId, ecran) => {
    if (!(SCREENS[screenId]?.legacy || []).length) { toast.error("Cet écran n'est pas disponible pour votre rôle"); return; }
    goAncienne(screenId, ecran);
    if (ecran) setTimeout(() => demanderOuverture({ kind: 'section', id: ecran, ecran }), 350);
    window.scrollTo({ top: 0 });
  }, [goAncienne]);
  const goScreenRef = useRef(goScreen); goScreenRef.current = goScreen;
  // Chemin de l'arborescence (ex. cabinet/planning) → écran
  const go = useCallback((target, ecran) => {
    const hit = findPage(tree, target);
    if (!hit) { toast.error("Cette page n'est pas disponible pour votre rôle"); return; }
    if (hit.page.lien) { go(hit.page.lien, ecran); return; }
    goScreen(hit.page.screens[0], ecran);
  }, [tree, goScreen]);
  // Ancienne action (tab, sous-onglet) → écran
  const legacyGo = useCallback((tab, sub) => goScreenRef.current(resolveLegacy(tab, sub)), []);
  // Ouverture d'un écran précis depuis un composant (ex. Connexion Doctipro → Codification)
  useEffect(() => {
    const onGo = (e) => { if (e.detail?.screen) goScreenRef.current(e.detail.screen, e.detail.ecran || null); };
    window.addEventListener('op-go-screen', onGo);
    return () => window.removeEventListener('op-go-screen', onGo);
  }, []);
  // Bouton « OP » : ouvre la page d'accueil ; un 2e appui revient aux actualités
  const allerActualites = useCallback(() => { setAccueil(false); setActiveTab('actualites'); setActualitesSubTab('news'); window.scrollTo({ top: 0 }); }, []);
  const toggleAccueil = useCallback(() => { if (accueil) allerActualites(); else { setAccueil(true); window.scrollTo({ top: 0 }); } }, [accueil, allerActualites]);
  const choisirOnglet = useCallback((tab) => { setAccueil(false); setActiveTab(tab); }, []);

  // Clic sur une notification push → page + élément concerné
  const ouvrirDepuisNotif = useCallback((data) => {
    const r = routeForNotif(data || {});
    if (!r) return;
    goAncienne(r.screen, r.ecran);
    if (r.chat) { setTimeout(() => window.dispatchEvent(new CustomEvent('open-chat', { detail: r.chat })), 400); return; }
    if (r.kind && r.id) setTimeout(() => demanderOuverture({ kind: r.kind, id: String(r.id), ecran: r.ecran || null }), 350);
    else if (r.ecran) setTimeout(() => demanderOuverture({ kind: 'section', id: r.ecran, ecran: r.ecran }), 350);
  }, [goAncienne]);
  const ouvrirDepuisNotifRef = useRef(ouvrirDepuisNotif); ouvrirDepuisNotifRef.current = ouvrirDepuisNotif;

  // App ouverte depuis une notification (fenêtre fermée) : ?notif=<json>
  const notifParamDone = useRef(false);
  useEffect(() => {
    if (!user || !droitsReady || notifParamDone.current) return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('notif');
    if (!raw) return;
    notifParamDone.current = true;
    let data = null;
    try { data = JSON.parse(raw); } catch { data = null; }
    window.history.replaceState({}, '', window.location.pathname);
    if (data) setTimeout(() => ouvrirDepuisNotifRef.current(data), 600);
  }, [user, tree, droitsReady]);

  // Raccourci clavier global Cmd/Ctrl + K → ouvre la palette (Directeur uniquement)
  useEffect(() => {
    const isAdmin = estDirecteur(user, 'admin');
    if (!isAdmin) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCmdKOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [user?.role]);

  // Mapping tab → setter de sous-onglet (utilisé par la palette)
  const handleCmdKAction = useCallback((act) => {
    if (act.type === 'logout') { logout?.(); return; }
    if (act.type === 'open-planning-diagnostic') { setDiagOpen(true); return; }
    setAccueil(false);
    if (act.type === 'tab') { setActiveTab(act.tab); return; }
    if (act.type === 'subtab') {
      setActiveTab(act.parentTab);
      if (act.parentTab === 'bloc') setBlocSubTab(act.subTab);
      else if (act.parentTab === 'ressources') {
        // Ressources a 2 systèmes de sous-onglets selon legacy
        setRessourcesNewSubTab(act.subTab);
        setRessourcesSubTab(act.subTab);
      }
      else if (act.parentTab === 'actualites') setActualitesSubTab(act.subTab);
      else if (act.parentTab === 'comptabilite') setComptabiliteSubTab(act.subTab);
      else if (act.parentTab === 'equipe') setEquipeSubTab(act.subTab);
    }
  }, [logout]);

  // Avertissements popup
  const [pendingAvertissements, setPendingAvertissements] = useState([]);
  const [currentAvertIdx, setCurrentAvertIdx] = useState(0);
  const [avertReponse, setAvertReponse] = useState('');
  const [avertSigning, setAvertSigning] = useState(false);
  const [pendingReglements, setPendingReglements] = useState([]);
  const [currentRegIdx, setCurrentRegIdx] = useState(0);
  const [regSigning, setRegSigning] = useState(false);

  // Check for pending avertissements and reglements on login
  useEffect(() => {
    if (user) {
      axios.get(`${API}/avertissements/pending`).then(res => {
        setPendingAvertissements(res.data || []);
        setCurrentAvertIdx(0);
        setAvertReponse('');
      }).catch(() => {});
      axios.get(`${API}/reglements/pending`).then(res => {
        setPendingReglements(res.data || []);
        setCurrentRegIdx(0);
      }).catch(() => {});
    }
  }, [user?.id]);

  const currentAvert = pendingAvertissements[currentAvertIdx] || null;
  const showAvertPopup = pendingAvertissements.length > 0 && currentAvertIdx < pendingAvertissements.length;

  const handleAvertRespond = async () => {
    if (!currentAvert) return;
    if (avertReponse.trim()) {
      try {
        await axios.put(`${API}/avertissements/${currentAvert.id}/respond`, { reponse: avertReponse.trim() });
      } catch {}
    }
  };

  const handleAvertSign = async () => {
    if (!currentAvert) return;
    setAvertSigning(true);
    try {
      // Send response first if provided
      if (avertReponse.trim() && !currentAvert.reponse) {
        await axios.put(`${API}/avertissements/${currentAvert.id}/respond`, { reponse: avertReponse.trim() });
      }
      await axios.put(`${API}/avertissements/${currentAvert.id}/sign`);
      toast.success('Avertissement signé');
      // Move to next or close
      setAvertReponse('');
      if (currentAvertIdx + 1 < pendingAvertissements.length) {
        setCurrentAvertIdx(prev => prev + 1);
      } else {
        setPendingAvertissements([]);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setAvertSigning(false); }
  };

  const currentReg = pendingReglements[currentRegIdx] || null;
  const showRegPopup = !showAvertPopup && pendingReglements.length > 0 && currentRegIdx < pendingReglements.length;

  const handleRegSign = async () => {
    if (!currentReg) return;
    setRegSigning(true);
    try {
      await axios.put(`${API}/reglements/${currentReg.id}/sign`);
      toast.success('Règlement signé');
      if (currentRegIdx + 1 < pendingReglements.length) {
        setCurrentRegIdx(prev => prev + 1);
      } else {
        setPendingReglements([]);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setRegSigning(false); }
  };

  useEffect(() => {
    const handleNavigateToChat = () => choisirOnglet('messages');
    const handleNavigateToTab = (e) => choisirOnglet(e.detail);
    const handleNavigateToComptaSubtab = (e) => { setComptabiliteSubTab(e.detail || 'especes'); choisirOnglet('comptabilite'); };
    window.addEventListener('navigate-to-chat', handleNavigateToChat);
    window.addEventListener('navigate-to-tab', handleNavigateToTab);
    window.addEventListener('navigate-to-compta-subtab', handleNavigateToComptaSubtab);
    
    // Note RGPD : les notifications push utilisent désormais Web Push VAPID (chiffré E2E).
    // Le Service Worker affiche les notifications, y compris au premier plan.
    
    // Écouter les clics de notification push (Service Worker) → page + élément concerné
    const handleSWMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') ouvrirDepuisNotifRef.current(event.data.data || {});
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    
    // Lire les URL params au chargement (quand l'app est ouverte depuis une notif)
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab')) {
      setActiveTab(params.get('tab'));
      if (params.get('sub')) setRessourcesSubTab(params.get('sub'));
      if (params.get('chat')) {
        setTimeout(() => window.dispatchEvent(new CustomEvent('open-chat', { detail: {
          message_type: params.get('chat')?.toUpperCase(),
          sender_id: params.get('user'),
          groupe_id: params.get('groupe')
        }})), 500);
      }
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    return () => {
      window.removeEventListener('navigate-to-chat', handleNavigateToChat);
      window.removeEventListener('navigate-to-tab', handleNavigateToTab);
      window.removeEventListener('navigate-to-compta-subtab', handleNavigateToComptaSubtab);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const getMenuItems = () => {
    const isDirector = estDirecteur(user, 'admin');
    const isManager = user?.role === 'Manager';

    const canView = (rubrique) => isDirector || hasDroit(user, rubrique, 'voir');

    const items = [];

    // Onglets de base — filtrés par autorisations
    if (canView('actualites')) items.push({ id: 'actualites', label: 'Actualités', icon: Bell });
    if (canView('personnel')) items.push({ id: 'personnel', label: 'Équipe', icon: Users });
    if (canView('planning')) items.push({ id: 'planning', label: 'Planning', icon: Calendar });
    if (canView('conges')) items.push({ id: 'conges', label: 'Congés', icon: Clock });
    if (canView('messages')) items.push({ id: 'messages', label: 'Messages', icon: MessageSquare });
    if (canView('demandes_creneaux')) items.push({ id: 'demandes-travail', label: 'Demande de créneaux', icon: CalendarDays });
    if (canView('coffre_fort')) items.push({ id: 'documents', label: 'Mon Coffre-Fort', icon: FileText });
    if (canView('ressources') || canView('bloc_operatoire') || canView('stocks') || canView('stocks_global') || canView('materiel_bloc')) items.push({ id: 'bloc', label: 'Bloc', icon: Stethoscope });
    if (canView('ressources_docs') || canView('tutos') || canView('infos_patient') || canView('transcription') || canView('preconsult') || canView('champ_visuel') || canView('demande_angiographie') || canView('demande_paupiere') || canView('demande_ipl') || canView('stocks') || canView('stocks_global') || canView('materiel_inventaire') || canView('notebook') || canView('notes') || canView('aide_decision') || canView('projets') || canView('curriculum_vitae') || canView('recap_mensuel') || canView('primes')) items.push({ id: 'ressources', label: 'Ressources', icon: FolderOpen });
    if (canView('comptabilite') || canView('registre_factures') || canView('coffre_fort_factures') || canView('recuperation_especes') || canView('compta_dashboard') || canView('caisse_factures') || canView('analyses_annuelles') || canView('frais_cabinet') || canView('bons_livraison') || canView('tiers_payant')) items.push({ id: 'comptabilite', label: 'Comptabilité', icon: BarChart3 });

    // Salles, Plan Cabinet
    if (canView('salles')) items.push({ id: 'salles', label: 'Gestion Salles', icon: Building2 });
    if (isDirector || isManager) items.push({ id: 'plan-cabinet', label: 'Plan Cabinet', icon: MapPin });

    // Admin et Autorisations — Directeur uniquement
    if (canView('admin')) items.push({ id: 'admin', label: 'Administration', icon: Settings });
    if (isDirector) {
      items.push({ id: 'autorisations', label: 'Réglages employé', icon: Shield });
      items.push({ id: 'centres', label: 'Gestion Centres', icon: Building2 });
      items.push({ id: 'notifications-admin', label: 'Notifications', icon: Send });
      items.push({ id: 'rgpd', label: 'RGPD', icon: Scale });
      items.push({ id: 'bornes', label: 'Bornes & QR code', icon: QrCode });
      items.push({ id: 'autres-parametres', label: 'Autres paramètres', icon: Settings });
    }
    if (canView('securite')) items.push({ id: 'pare-feu', label: 'Sécurité', icon: Shield });

    return items;
  };

  // Helper pour vérifier les permissions de modification
  const canModifyRubrique = (rubrique) => {
    if (!user) return false;
    if (estDirecteur(user, 'admin')) return true;
    const auth = user.autorisations?.[rubrique];
    if (auth) return auth.modifier || auth.droits_directeur || auth.modification_directeur;
    return false;
  };

  const canViewSubRubrique = (rubrique) => hasDroit(user, rubrique, 'voir');

  const renderContent = () => {
    switch (activeTab) {
      case 'actualites':
        return (
          <div data-testid="actualites-tabs-container">
            {/* Sub-tabs navigation */}
            <div className="flex flex-wrap gap-1 mb-4 sm:mb-6 bg-white/80 backdrop-blur-sm rounded-xl p-1 sm:p-1.5 shadow-sm border border-gray-100" data-testid="actualites-subtabs">
              {[
                { id: 'news', label: 'Actualités', icon: Bell, rubrique: 'actualites_news' },
                { id: 'a-traiter', label: 'À traiter', icon: Inbox, rubrique: 'dossiers_a_traiter' },
                { id: 'taches', label: 'Tâches du jour', icon: ClipboardList, rubrique: 'taches_jour' },
                { id: 'accueil-atcd', label: 'Accueil / ATCD', icon: QrCode, rubrique: 'accueil_atcd' },
                { id: 'plan-cabinet', label: 'Plan du cabinet', icon: MapPin, rubrique: 'plan_cabinet' },
                { id: 'quizz', label: 'Quizz', icon: BarChart3, rubrique: 'quizz' },
                ...(estDirecteur(user, 'admin') || hasDroit(user, 'avertissements', 'emettre') || user?.autorisations?.avertissements?.droits_directeur || user?.autorisations?.taches_jour?.droits_directeur
                  ? [
                      { id: 'avertissements', label: 'Avertissements', icon: AlertTriangle, rubrique: 'avertissements' },
                      { id: 'reglements', label: 'Règlement', icon: ScrollText, rubrique: 'avertissements' },
                    ]
                  : []),
              ].filter(tab => canViewSubRubrique(tab.rubrique)).map(tab => {
                const Icon = tab.icon;
                const isActive = actualitesSubTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActualitesSubTab(tab.id)}
                    data-testid={`subtab-${tab.id}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-[#1173D4] text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                    }`}
                  >
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {/* Sub-tab content */}
            {actualitesSubTab === 'news' && <ActualitesManager user={user} centreActif={centreActif} corrHabilite={corrHabilite} onSubTabChange={setActualitesSubTab} onNavigate={(tab, subTab) => { setActiveTab(tab); if (tab === 'comptabilite' && subTab) setComptabiliteSubTab(subTab); if (tab === 'bloc' && subTab) setBlocSubTab(subTab); }} />}
            {actualitesSubTab === 'a-traiter' && <DossiersATraiterManager user={user} corrHabilite={corrHabilite} />}
            {actualitesSubTab === 'plan-cabinet' && <CabinetPlanWithPopup user={user} centreActif={centreActif} />}
            {actualitesSubTab === 'quizz' && <QuizzManager user={user} />}
            {actualitesSubTab === 'taches' && <TachesDuJourManager />}
            {actualitesSubTab === 'accueil-atcd' && <AccueilAtcdManager />}
            {actualitesSubTab === 'avertissements' && <AvertissementsManager />}
            {actualitesSubTab === 'reglements' && <ReglementsManager />}
          </div>
        );
      case 'profil':
        return <MonProfilManager />;
      case 'personnel':
        return (
          <div data-testid="equipe-container">
            <div className="flex flex-wrap gap-1 mb-4 sm:mb-6 bg-white/80 backdrop-blur-sm rounded-xl p-1 sm:p-1.5 shadow-sm border border-gray-100">
              {[
                { id: 'personnel', label: 'Personnel', icon: Users, rubrique: 'personnel' },
                { id: 'annuaire', label: 'Annuaire', icon: Phone, rubrique: 'annuaire' },
                { id: 'comptes_mdp', label: 'Comptes & MDP', icon: KeyRound, rubrique: 'comptes_mdp' },
                { id: 'cartes', label: 'Mes cartes', icon: CreditCard, rubrique: 'comptes_mdp' },
                ...(corrHabilite ? [{ id: 'correspondants', label: 'Correspondants', icon: Stethoscope, rubrique: 'personnel' }] : []),
              ].filter(tab => canViewSubRubrique(tab.rubrique)).map(tab => {
                const Icon = tab.icon;
                const isActive = equipeSubTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setEquipeSubTab(tab.id)}
                    data-testid={`equipe-subtab-${tab.id}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-[#1173D4] text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                    }`}
                  >
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {equipeSubTab === 'personnel' && <PersonnelManager />}
            {equipeSubTab === 'annuaire' && <AnnuaireManager />}
            {equipeSubTab === 'comptes_mdp' && <ComptesMdpManager user={user} />}
            {equipeSubTab === 'cartes' && <CartesManager user={user} />}
            {equipeSubTab === 'correspondants' && corrHabilite && <CorrespondantsManager />}
          </div>
        );
      case 'planning':
        return <PlanningManager />;
      case 'conges':
        return <CongeManager user={user} />;
      case 'taches-jour':
        // Redirect to actualites with taches sub-tab
        if (actualitesSubTab !== 'taches') setActualitesSubTab('taches');
        setActiveTab('actualites');
        return null;
      case 'demandes-travail':
        return <DemandesTravailManager />;
      case 'semaines-types':
        return <SemainesTypesManager />;
      case 'messages':
        return <ChatManager user={user} />;
      case 'documents':
        return <CoffreFortManager />;
      case 'plan-cabinet':
        return <PlanCabinetManager />;
      case 'stocks':
        return <StocksManager />;
      case 'tutos':
        return <DocsTutosManager />;
      case 'bloc': {
        const blocTabs = [
          { id: 'bloc-op', label: 'Bloc Opératoire', icon: Stethoscope, rubrique: 'bloc_operatoire' },
          { id: 'materiel', label: 'Matériel Bloc', icon: Package, rubrique: 'materiel_bloc' },
          { id: 'patients-adresser', label: 'Patients à adresser', icon: UserPlus, rubrique: 'bloc_operatoire' },
          { id: 'demandes-examen', label: 'Demandes d\'examen', icon: ClipboardList, rubrique: 'bloc_operatoire' },
          { id: 'iol-calculator', label: 'Calculateur IOL', icon: Calculator, rubrique: 'bloc_operatoire' },
          { id: 'indication-refractive', label: 'Indication réfractive', icon: Stethoscope, rubrique: 'bloc_operatoire' },
        ].filter(tab => canViewSubRubrique(tab.rubrique));
        const blocAllowed = new Set(blocTabs.map(t => t.id));
        if (canViewSubRubrique('bloc_operatoire')) blocAllowed.add('programmation');
        // Onglet demandé non autorisé (ex. assistante sans accès Bloc opératoire) → 1er onglet visible, pas d'appels 403
        const blocSubTab = blocAllowed.has(blocSubTabRaw) ? blocSubTabRaw : (blocTabs[0]?.id || null);
        return (
          <div data-testid="bloc-tabs-container">
            {!blocSubTab && <p className="text-sm text-gray-500 bg-white/80 rounded-xl p-4" data-testid="bloc-no-access">Vous n'avez pas accès aux rubriques du Bloc. Demandez au directeur d'activer « Bloc opératoire » dans vos autorisations.</p>}
            <div className="flex flex-wrap gap-1 mb-4 sm:mb-6 bg-white/80 backdrop-blur-sm rounded-xl p-1 sm:p-1.5 shadow-sm border border-gray-100" data-testid="bloc-subtabs">
              {blocTabs.map(tab => {
                const Icon = tab.icon;
                const isActive = blocSubTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => setBlocSubTab(tab.id)} data-testid={`bloctab-${tab.id}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#1173D4] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'}`}>
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />{tab.label}
                  </button>
                );
              })}
            </div>
            {(blocSubTab === 'bloc-op' || blocSubTab === 'programmation') && <BlocOperatoireManager key={blocSubTab} user={user} initialView={blocSubTab === 'programmation' ? 'programmation' : undefined} />}
            {blocSubTab === 'materiel' && <MaterielBlocManager user={user} />}
            {blocSubTab === 'patients-adresser' && <PatientsAdresserManager user={user} />}
            {blocSubTab === 'demandes-examen' && <DemandesExamenManager user={user} />}
            {blocSubTab === 'iol-calculator' && <IOLCalculator />}
            {blocSubTab === 'indication-refractive' && <RefractiveDecisionTool />}
          </div>
        );
      }
      case 'ressources':
        return (
          <div data-testid="ressources-tabs-container">
            <div className="flex flex-wrap gap-1 mb-4 sm:mb-6 bg-white/80 backdrop-blur-sm rounded-xl p-1 sm:p-1.5 shadow-sm border border-gray-100" data-testid="ressources-subtabs">
              {[
                { id: 'bibliotheque', label: 'Bibliothèque & Docs', icon: BookOpen, rubrique: ['tutos', 'notebook'] },
                { id: 'stocks', label: 'Gestion Stocks', icon: Package, rubrique: 'stocks' },
                { id: 'stock-global', label: 'Réserves des stocks', icon: Package, rubrique: 'stocks_global' },
                { id: 'liste-materiel', label: 'Liste matériel', icon: Package, rubrique: 'materiel_inventaire' },
                { id: 'champ-visuel', label: "Demande d'examen", icon: Eye, rubrique: ['champ_visuel', 'demande_angiographie', 'demande_paupiere', 'demande_ipl'] },
                { id: 'infos-patient', label: 'Informations patient', icon: FileText, rubrique: 'infos_patient' },
                { id: 'ordonnances', label: 'Ordonnances', icon: ScrollText, rubrique: 'ordonnances' },
                { id: 'connexion-doctipro', label: 'Connexion Doctipro', icon: Layers, rubrique: 'connexion_doctipro' },
                { id: 'notes', label: 'Notes', icon: StickyNote, rubrique: 'notes' },
                { id: 'aide-decision', label: 'Aide décision', icon: Brain, rubrique: 'aide_decision' },
                { id: 'strabisme', label: 'Simulateur strabisme', icon: Eye, rubrique: 'aide_decision' },
                { id: 'anatomie-3d', label: 'Anatomie 3D œil', icon: Eye, rubrique: 'aide_decision' },
                { id: 'ocr-ordonnance', label: 'OCR Ordonnance', icon: ScrollText, rubrique: 'aide_decision' },
                { id: 'preconsult', label: 'PréConsult', icon: ClipboardList, rubrique: ['preconsult', 'transcription'] },
                { id: 'projets', label: 'Projets', icon: Layers, rubrique: 'projets' },
                { id: 'curriculum-vitae', label: 'Curriculum Vitae', icon: FileText, rubrique: 'curriculum_vitae' },
                { id: 'recap-mensuel', label: 'Récap mensuel', icon: ClipboardList, rubrique: 'recap_mensuel' },
                { id: 'primes', label: 'Primes', icon: Star, rubrique: 'primes' },
                { id: 'grille-salariale', label: 'Grille salariale', icon: Euro, rubrique: null },
                ...(qualiteHabilite ? [{ id: 'qualite', label: 'Suivi qualité médecins', icon: Gauge, rubrique: null }] : []),
                { id: 'legionellose', label: 'Légionellose', icon: Droplets, rubrique: null },
                { id: 'facturation', label: 'Codification', icon: Euro, rubrique: null },
                { id: 'icd', label: 'ICD', icon: Stethoscope, rubrique: null },
                { id: 'ishihara', label: 'Ishihara', icon: Eye, rubrique: null },
                { id: 'osdi', label: 'Sécheresse oculaire (OSDI)', icon: Droplets, rubrique: null },
                { id: 'commentaire-direction', label: 'Commentaire à la direction', icon: MessageSquare, rubrique: null },
              ].filter(tab => tab.rubrique === null ? true : (Array.isArray(tab.rubrique) ? tab.rubrique.some(r => canViewSubRubrique(r)) : canViewSubRubrique(tab.rubrique))).sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })).map(tab => {
                const Icon = tab.icon;
                const isActive = ressourcesNewSubTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => setRessourcesNewSubTab(tab.id)} data-testid={`restab-${tab.id}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#1173D4] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'}`}>
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />{tab.label}
                  </button>
                );
              })}
            </div>
            {(ressourcesNewSubTab === 'bibliotheque' || ressourcesNewSubTab === 'tutos' || ressourcesNewSubTab === 'notebook') && <BibliothequeManager user={user} />}
            {ressourcesNewSubTab === 'stocks' && <StocksManager />}
            {ressourcesNewSubTab === 'stock-global' && <StockGlobalManager user={user} />}
            {ressourcesNewSubTab === 'liste-materiel' && <MaterielInventaireManager user={user} />}
            {['champ-visuel', 'examens-angiographie', 'examens-paupiere', 'examens-ipl'].includes(ressourcesNewSubTab) && <DemandeExamenHub user={user} initial={ressourcesNewSubTab} />}
            {ressourcesNewSubTab === 'infos-patient' && <InfosPatientManager user={user} />}
            {ressourcesNewSubTab === 'ordonnances' && <OrdonnancesManager />}
            {ressourcesNewSubTab === 'connexion-doctipro' && <ConnexionDoctiproManager />}
            {ressourcesNewSubTab === 'notes' && <NotesManager user={user} />}
            {ressourcesNewSubTab === 'aide-decision' && <AideDecisionManager />}
            {ressourcesNewSubTab === 'strabisme' && <StrabismusSimulator />}
            {ressourcesNewSubTab === 'anatomie-3d' && <EyeAnatomy3D />}
            {ressourcesNewSubTab === 'ocr-ordonnance' && <OCROrdonnance />}
            {(ressourcesNewSubTab === 'preconsult' || ressourcesNewSubTab === 'transcription') && <PreconsultManager iaAccess={canViewSubRubrique('transcription')} />}
            {ressourcesNewSubTab === 'projets' && <ProjetsManager />}
            {ressourcesNewSubTab === 'curriculum-vitae' && <CurriculumVitaeManager />}
            {ressourcesNewSubTab === 'recap-mensuel' && <RecapMensuelManager />}
            {ressourcesNewSubTab === 'primes' && <PrimesManager />}
            {ressourcesNewSubTab === 'grille-salariale' && <GrilleSalarialeManager />}
            {ressourcesNewSubTab === 'qualite' && qualiteHabilite && <QualiteManager acces={qualiteHabilite} />}
            {ressourcesNewSubTab === 'legionellose' && <LegionelloseManager user={user} />}
            {ressourcesNewSubTab === 'facturation' && <FacturationHtml user={user} />}
            {ressourcesNewSubTab === 'icd' && <IcdHtml user={user} />}
            {ressourcesNewSubTab === 'ishihara' && <IshiharaReleve />}
            {ressourcesNewSubTab === 'osdi' && <OsdiQuestionnaire />}
            {ressourcesNewSubTab === 'commentaire-direction' && <CommentaireDirection user={user} />}
          </div>
        );
      case 'salles':
        return <SallesManager />;
      case 'admin':
        return <AdminManager />;
      case 'autres-parametres':
        return <AutresParametres />;
      case 'bornes':
        return <BornesParametres />;
      case 'autorisations':
        return <AutorisationsManager key={autorisationsTab || 'edit'} initialTab={autorisationsTab} />;
      case 'espace':
        return <EspaceTravail user={user} espace={espace} corrHabilite={corrHabilite} centreActif={centreActif} onConfig={() => { setAutorisationsTab('espaces'); setActiveTab('autorisations'); }} />;
      case 'centres':
        return <CentresManager />;
      case 'comptabilite':
        return (
          <div data-testid="comptabilite-tabs-container">
            <div className="flex flex-wrap gap-1 mb-4 sm:mb-6 bg-white/80 backdrop-blur-sm rounded-xl p-1 sm:p-1.5 shadow-sm border border-gray-100" data-testid="comptabilite-subtabs">
              {(() => {
                // Médecin sans accès complet à la comptabilité → uniquement Registre
                const isDir = estDirecteur(user, 'admin');
                const autos = user?.autorisations || {};
                const fullCompta = isDir || !!(autos.comptabilite?.voir || autos.comptabilite?.droits_directeur);
                const canViewRegistre = fullCompta
                  || !!(autos.registre_factures?.voir || autos.registre_factures?.droits_directeur)
                  || user?.role === 'Médecin';
                const sub = k => !!(autos[k]?.voir || autos[k]?.droits_directeur);
                const allTabs = [
                  { id: 'analytics', label: 'Tableau de bord', icon: BarChart3, allow: fullCompta || sub('compta_dashboard') },
                  { id: 'caisse-factures', label: 'Caisse et factures', icon: Receipt, allow: fullCompta || sub('caisse_factures') },
                  { id: 'analyses-annuelles', label: 'Analyses annuelles', icon: TrendingUp, allow: fullCompta || sub('analyses_annuelles') },
                  { id: 'registre', label: 'Registre factures', icon: ScrollText, allow: canViewRegistre },
                  { id: 'frais', label: 'Frais cabinet', icon: Package, allow: fullCompta || sub('frais_cabinet') },
                  { id: 'bons-livraison', label: 'Bons de livraison', icon: Truck, allow: fullCompta || sub('bons_livraison') },
                  { id: 'especes', label: 'Récupération espèces', icon: HandCoins, allow: fullCompta || (autos.recuperation_especes ? sub('recuperation_especes') : true) },
                  { id: 'tiers-payant', label: 'Tiers Payant', icon: ScrollText, allow: fullCompta || sub('tiers_payant') },
                ];
                const visible = allTabs.filter(t => t.allow);
                // Si l'onglet courant n'est pas accessible → bascule sur le premier visible
                if (visible.length && !visible.some(t => t.id === comptabiliteSubTab)) {
                  setTimeout(() => setComptabiliteSubTab(visible[0].id), 0);
                }
                return visible;
              })().map(tab => {
                const Icon = tab.icon;
                const isActive = comptabiliteSubTab === tab.id;
                return (
                  <button key={tab.id} onClick={() => setComptabiliteSubTab(tab.id)} data-testid={`compta-subtab-${tab.id}`}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-lg text-[11px] sm:text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#1173D4] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'}`}>
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />{tab.label}
                  </button>
                );
              })}
            </div>
            <ComptabiliteManager activeSubTab={comptabiliteSubTab} />
          </div>
        );
      case 'notifications-admin':
        return <NotificationsAdminManager />;
      case 'rgpd':
        return <RgpdManager />;
      case 'pare-feu':
        return <PareFeuManager />;
      case 'connexion-doctipro':
        return <ConnexionDoctiproManager />;
      default:
        return null;
    }
  };

  const renderPopups = () => (
    <>
      <PointageRappel user={user} />
      {/* === POPUP AVERTISSEMENT À LA CONNEXION === */}
      <Dialog open={showAvertPopup} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={e => e.preventDefault()} onEscapeKeyDown={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" /> Avertissement
              {pendingAvertissements.length > 1 && (
                <Badge variant="secondary" className="text-xs ml-2">{currentAvertIdx + 1}/{pendingAvertissements.length}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {currentAvert && (
            <div className="space-y-4">
              {currentAvert.tache_titre && (
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <p className="text-xs text-gray-500">Tâche concernée</p>
                  <p className="text-sm font-medium text-gray-800">{currentAvert.tache_titre}</p>
                </div>
              )}

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-600 font-medium mb-1">Message de {currentAvert.created_by_name} :</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{currentAvert.message}</p>
                <p className="text-[10px] text-gray-400 mt-2">
                  {currentAvert.date_creation ? new Date(currentAvert.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>

              {currentAvert.image && (
                <div className="mt-2">
                  <img src={currentAvert.image} alt="Pièce jointe" className="max-h-48 rounded-lg border border-gray-200" />
                </div>
              )}

              {!currentAvert.reponse && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Votre réponse (facultative)</label>
                  <Textarea
                    placeholder="Répondre à l'avertissement..."
                    value={avertReponse}
                    onChange={e => setAvertReponse(e.target.value)}
                    rows={3}
                    className="mt-1"
                    data-testid="avert-popup-response"
                  />
                </div>
              )}

              <Button
                onClick={handleAvertSign}
                disabled={avertSigning}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-medium"
                data-testid="avert-popup-sign-btn"
              >
                {avertSigning ? 'Signature en cours...' : "J'ai lu et signé"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === POPUP RÈGLEMENT À LA CONNEXION === */}
      <Dialog open={showRegPopup} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={e => e.preventDefault()} onEscapeKeyDown={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1173D4]">
              <ScrollText className="h-5 w-5" /> Règlement
              {pendingReglements.length > 1 && (
                <Badge variant="secondary" className="text-xs ml-2">{currentRegIdx + 1}/{pendingReglements.length}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {currentReg && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-[#1173D4] font-medium mb-1">Règlement de {currentReg.created_by_name} :</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{currentReg.message}</p>
                <p className="text-[10px] text-gray-400 mt-2">
                  {currentReg.date_creation ? new Date(currentReg.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
              <Button
                onClick={handleRegSign}
                disabled={regSigning}
                className="w-full bg-[#1173D4] hover:bg-[#007a9e] text-white font-medium"
                data-testid="reg-popup-sign-btn"
              >
                {regSigning ? 'Signature en cours...' : "J'ai lu et signé"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  return (
    <div className={`min-h-screen relative ${NUIT ? 'opn-v-futuriste' : ''}`} data-testid={`nav-variant-${navMode}`}>
      {/* Fond Ophtalmologie */}
      <div className="ophta-background">
        <div className="ophta-decoration ophta-decoration-1"></div>
        <div className="ophta-decoration ophta-decoration-2"></div>
        <div className="ophta-decoration ophta-decoration-3"></div>
        <div className="ophta-eye-pattern"></div>
        <div className="ophta-eye-pattern-2"></div>
      </div>
      
      <div className="app-content-wrapper pt-28">
        <Navigation menuOpen={menuOpen} setMenuOpen={setMenuOpen} menuItems={getMenuItems()} activeTab={accueil ? null : activeTab} setActiveTab={choisirOnglet} onOpenCmdK={() => setCmdKOpen(true)} onRaccourciAction={handleCmdKAction}
          accueilActif={accueil} onToggleAccueil={toggleAccueil} onActualites={allerActualites} espaceBadge={espace.total} onEspace={allerEspace} />
        
        <div className="w-full px-3 sm:px-6 lg:px-10 xl:px-12 py-4 sm:py-8">
          {/* Main Content - Full width sans sidebar */}
          <div className="w-full">
            <ConsignesBandeau />
            <NotificationToday />
            <Suspense fallback={<ModuleLoader />}>
              {accueil ? (
                <div className={NUIT ? '' : 'opn-v-nouvelle'} data-testid={`accueil-${navMode}`}>
                  {NUIT
                    ? <NavHomeFutur tree={tree} user={user} badges={badges} onGo={go} onGoScreen={goScreen} />
                    : <NavHome tree={tree} user={user} badges={badges} onGo={go} onGoScreen={goScreen} corrHabilite={corrHabilite} />}
                </div>
              ) : (
                <>
                  {estDirecteur(user, 'admin') && (() => {
                    const subs = { bloc: blocSubTabRaw, ressources: ressourcesNewSubTab, comptabilite: comptabiliteSubTab, actualites: actualitesSubTab };
                    const sid = resolveLegacy(activeTab, subs[activeTab]);
                    return sid ? <div className="flex justify-end mb-2" data-testid="rubrique-access-bar"><RubriqueAccess user={user} screenId={sid} /></div> : null;
                  })()}
                  {renderContent()}
                </>
              )}
            </Suspense>
          </div>
        </div>
      </div>

      {/* === Palette de commandes Ctrl/Cmd+K === */}
      <CommandPalette
        open={cmdKOpen}
        onClose={() => setCmdKOpen(false)}
        onAction={handleCmdKAction}
      />

      {/* === Modale de diagnostic Planning (assignations incohérentes) === */}
      {diagOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[5vh] px-3"
          onClick={() => setDiagOpen(false)}
        >
          <div
            className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">Diagnostic Planning</h2>
              <button
                onClick={() => setDiagOpen(false)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-slate-100 text-xl"
                aria-label="Fermer"
              >×</button>
            </div>
            <div className="p-4">
              <Suspense fallback={<ModuleLoader />}>
                <PlanningDiagnostic />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* === POPUP FICHIER À SIGNER À LA CONNEXION === */}
      <Suspense fallback={null}>
        <FichierASignerPopup user={user} />
      </Suspense>

      {renderPopups()}
    </div>
  );
};



const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to={{ pathname: '/login', search: window.location.search }} replace />;
};



const PatientDoctiproFlottant = () => {
  const { user } = useAuth();
  if (!user || window.location.pathname.startsWith('/passerelle') || window.location.pathname.startsWith('/check-in') || window.location.pathname.startsWith('/atcd')) return null;
  return <DoctiproDock />;
};

const FloatingChatButton = ({ onNavigateToChat }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    if (!user) return;
    
    const fetchUnreadMessages = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-counts`);
        const data = response.data || {};
        setUnreadCount(data.total || 0);
        setRecentMessages(data.recent || []);
      } catch (error) {
        console.log('Erreur fetch unread');
      }
    };

    fetchUnreadMessages();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadMessages();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [user]);

  if (!user || user.role !== 'Correspondant') return null;

  const getUserAvatar = (userData) => {
    if (userData?.photo_url) {
      return <img src={getPhotoUrl(userData.photo_url)} alt="" />;
    }
    const bgColor = userData?.role === 'Médecin' ? 'bg-gradient-to-br from-blue-400 to-blue-600' :
                    userData?.role === 'Assistant' ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' :
                    'bg-gradient-to-br from-purple-400 to-purple-600';
    return (
      <div className={`avatar-fallback ${bgColor}`}>
        {userData?.prenom?.[0]}{userData?.nom?.[0]}
      </div>
    );
  };

  return (
    <>
      {/* Bouton flottant */}
      <button 
        className="chat-floating-button"
        onClick={() => setShowPreview(!showPreview)}
        data-testid="floating-chat-btn"
      >
        <MessageSquare />
        {unreadCount > 0 && (
          <span className="chat-floating-badge">{unreadCount}</span>
        )}
      </button>

      {/* Aperçu des messages non-lus */}
      {showPreview && (
        <div className="chat-floating-preview">
          <div className="chat-floating-preview-header">
            <span className="chat-floating-preview-title">
              {unreadCount > 0 ? `${unreadCount} message${unreadCount > 1 ? 's' : ''} non-lu${unreadCount > 1 ? 's' : ''}` : 'Messages'}
            </span>
            <button 
              className="chat-floating-preview-close"
              onClick={() => setShowPreview(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="chat-floating-preview-list">
            {recentMessages.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Aucun message non-lu
              </div>
            ) : (
              recentMessages.map((msg, idx) => (
                <div 
                  key={idx} 
                  className="chat-floating-preview-item"
                  onClick={() => {
                    setShowPreview(false);
                    if (onNavigateToChat) onNavigateToChat();
                  }}
                >
                  <div className="chat-floating-preview-avatar">
                    {msg.type === 'GENERAL' ? (
                      <div className="avatar-fallback bg-gradient-to-br from-blue-400 to-blue-600" style={{width:'36px',height:'36px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'14px'}}>G</div>
                    ) : msg.type === 'GROUPE' ? (
                      <div className="avatar-fallback bg-gradient-to-br from-orange-400 to-orange-600" style={{width:'36px',height:'36px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'14px'}}>Gr</div>
                    ) : (
                      getUserAvatar(msg.expediteur)
                    )}
                  </div>
                  <div className="chat-floating-preview-info">
                    <div className="chat-floating-preview-name">
                      {msg.label || `${msg.expediteur?.prenom} ${msg.expediteur?.nom}`}
                      <span style={{fontSize:'10px',color:'#6b7280',marginLeft:'6px'}}>
                        {msg.type === 'PRIVE' ? 'Privé' : msg.type === 'GENERAL' ? 'Général' : 'Groupe'}
                      </span>
                    </div>
                    <div className="chat-floating-preview-message">
                      {msg.last_message?.substring(0, 40)}{msg.last_message?.length > 40 ? '...' : ''}
                    </div>
                  </div>
                  <span style={{
                    background: msg.type === 'PRIVE' ? '#3b82f6' : msg.type === 'GENERAL' ? '#0d9488' : '#f97316',
                    color: 'white', borderRadius: '12px',
                    padding: '2px 8px', fontSize: '11px', fontWeight: 'bold'
                  }}>{msg.count}</span>
                </div>
              ))
            )}
          </div>
          {recentMessages.length > 0 && (
            <div 
              style={{ padding: '10px', textAlign: 'center', borderTop: '1px solid #e5e7eb', cursor: 'pointer', color: '#1173D4', fontWeight: '600', fontSize: '14px' }}
              onClick={() => { setShowPreview(false); if (onNavigateToChat) onNavigateToChat(); }}
            >
              Ouvrir la messagerie
            </div>
          )}
        </div>
      )}
    </>
  );
};


// Main App Component
function App() {
  return (
    <div className="App">
      {/* Arrière-plan ophtalmologique moderne */}
      <div className="app-background">
        <div className="bg-decoration bg-decoration-1"></div>
        <div className="bg-decoration bg-decoration-2"></div>
        <div className="bg-decoration bg-decoration-3"></div>
      </div>
      
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/check-in/*" element={<Suspense fallback={null}><BorneApp type="accueil" /></Suspense>} />
              <Route path="/atcd/*" element={<Suspense fallback={null}><BorneApp type="atcd" /></Suspense>} />
              <Route path="/passerelle" element={<Suspense fallback={null}><PasserellePage /></Suspense>} />
              <Route 
                path="/*" 
                element={
                  <ProtectedRoute>
                    <EspaceSelonRole />
                  </ProtectedRoute>
                } 
              />
            </Routes>
            <Toaster />
            <ChoixOngletDoctipro />
            <AlerteFactureImpayee />
            <PatientDoctiproFlottant />
            <FloatingChatButton onNavigateToChat={() => {
              window.dispatchEvent(new CustomEvent('navigate-to-chat'));
            }} />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;


